import { useMemo, useState } from 'react'

import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import { FloatingPortalToRoot } from '@inno-ui/z-index'
import { useQueries } from '@tanstack/react-query'
import { useGetPartnerResponsiblesQuery } from 'shared/api/afterSalesApi'
import { useGetMainAppointlyLinksQuery } from 'shared/api/appointlyApi'
import { GET_USER_QUERY_KEY, getUser } from 'shared/api/auth/requests'
import { hasInnoscriptaChampionResponsibility } from 'shared/components/MainLayout/MainLayout.utils'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'
import { useIsSffOrg } from 'shared/hooks/useIsSffOrg'
import { useUserContext } from 'shared/hooks/useUserContext'

import { INTERNAL_SUPPORT_CONTACTS } from './InnoscriptaContacts.constants'
import styles from './InnoscriptaContacts.module.scss'
import {
  findInnoscriptaChampion,
  getGeneralSupportContacts,
  groupPartnerResponsibles,
  hydrateInternalSupportContacts
} from './InnoscriptaContacts.utils'
import { ContactsPopup, ContactsTrigger } from './components'
import { useOpenContactChat } from './useOpenContactChat'

/**
 * The "Your innoscripta contacts" popup in the top dock.
 *
 * SFF partners see the people assigned to their organization. External
 * non-Champions additionally see their organization's Champion as the main
 * contact and under General Support. innoscripta's own organization has no
 * such assignments, so internal users see the local support contact list.
 */
export const InnoscriptaContacts = () => {
  const { user } = useUserContext()
  const isSffOrg = useIsSffOrg()
  const [isOpen, setIsOpen] = useState(false)
  const openContactChat = useOpenContactChat()

  const isInternalOrg =
    user?.organization_id === INNOSCRIPTA_INTERNAL_ORGANIZATION_ID
  const hasPartnerContacts = isSffOrg && !isInternalOrg

  const { data: responsibles } = useGetPartnerResponsiblesQuery({
    enabled: hasPartnerContacts
  })

  const internalUserQueries = useQueries({
    queries: Object.values(INTERNAL_SUPPORT_CONTACTS).map(contact => ({
      queryKey: [GET_USER_QUERY_KEY, contact.user.id],
      queryFn: () => getUser({ id: contact.user.id ?? -1 }),
      enabled: isInternalOrg && contact.user.id !== null
    }))
  })

  const hydratedInternalSupportContacts = useMemo(
    () =>
      hydrateInternalSupportContacts(
        internalUserQueries.map(query =>
          query.isSuccess ? query.data?.data : undefined
        )
      ),
    [internalUserQueries]
  )

  const internalProfilesResolved = internalUserQueries.every(
    query => query.isFetched
  )

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context)
  ])

  const contacts = useMemo(() => {
    if (isInternalOrg && internalProfilesResolved)
      return getGeneralSupportContacts(hydratedInternalSupportContacts)
    if (isInternalOrg) return null
    // The default contacts stand in for unassigned roles, not for an
    // unfinished request — so wait for the response before showing anyone.
    if (!hasPartnerContacts || !responsibles) return null

    const champion = findInnoscriptaChampion(responsibles)
    const isCurrentUserChampion = hasInnoscriptaChampionResponsibility(
      user?.id,
      responsibles
    )

    return groupPartnerResponsibles(
      responsibles,
      isCurrentUserChampion ? undefined : champion
    )
  }, [
    hasPartnerContacts,
    hydratedInternalSupportContacts,
    internalProfilesResolved,
    isInternalOrg,
    responsibles,
    user?.id
  ])

  const contactUserIds = useMemo(() => {
    if (!contacts) return []

    return [
      ...new Set(
        contacts.groups.flatMap(group =>
          group.contacts
            .map(contact => contact.responsible.user.id)
            .filter((id): id is number => id !== null)
        )
      )
    ]
  }, [contacts])

  const { data: appointlyLinks = [] } = useGetMainAppointlyLinksQuery(
    contactUserIds,
    { enabled: contactUserIds.length > 0 }
  )

  const contactsWithAppointlyLinks = useMemo(() => {
    if (!contacts) return null

    const linksByUserId = new Map(
      appointlyLinks.map(link => [String(link.user_id), link.url])
    )

    return {
      ...contacts,
      groups: contacts.groups.map(group => ({
        ...group,
        contacts: group.contacts.map(contact => ({
          ...contact,
          appointlyUrl:
            contact.responsible.user.id === null
              ? undefined
              : linksByUserId.get(String(contact.responsible.user.id))
        }))
      }))
    }
  }, [appointlyLinks, contacts])

  if (!contactsWithAppointlyLinks) return null

  const { mainContact, groups } = contactsWithAppointlyLinks

  return (
    <>
      <span className={styles.slot}>
        <ContactsTrigger
          ref={refs.setReference}
          isOpen={isOpen}
          mainContact={mainContact}
          {...getReferenceProps()}
        />
      </span>

      {isOpen && (
        <FloatingPortalToRoot type='floatingUI'>
          <div
            ref={refs.setFloating}
            className={styles.floating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <ContactsPopup
              groups={groups}
              onClose={() => setIsOpen(false)}
              onChatClick={authId => {
                void openContactChat(authId)
              }}
            />
          </div>
        </FloatingPortalToRoot>
      )}
    </>
  )
}
